import { Button, Head, Link, Typography } from "zudoku/components";

const firstRowProducts = [
  {
    label: "Remittance",
    description: "Cross-border money transfers with real-time tracking",
    href: "/remittance-api",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <path d="M22 12h-20M22 12l-6-6M22 12l-6 6M8 6l-6 6 6 6" />
      </svg>
    ),
  },
  {
    label: "Trade",
    description: "OTC trading workflows and settlement lifecycle",
    href: "/trade-api",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <path d="M3 3v18h18" />
        <path d="M7 16l4-8 4 4 5-9" />
      </svg>
    ),
  },
  {
    label: "Checkout",
    description: "Funds collection and hosted checkout flows",
    href: "/checkout-api",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <path d="M1 10h22" />
      </svg>
    ),
  },
];

const secondRowProducts = [
  {
    label: "Wallet",
    description: "Vault management, deposits, and withdrawals",
    href: "/wallet-api",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
        <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
      </svg>
    ),
  },
  {
    label: "Payout",
    description: "Destination wallets and outbound crypto payouts",
    href: "/payout-api",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="3" />
        <path d="M6 6v-1a1 1 0 0 1 1-1h4" />
      </svg>
    ),
  },
];

const quickLinks = [
  {
    label: "Integration Setup",
    href: "/getting-started",
    description: "Generate credentials and sandbox access",
  },
  {
    label: "Authentication",
    href: "/authentication",
    description: "HMAC signing and idempotency keys",
  },
  {
    label: "Webhooks",
    href: "/webhooks",
    description: "Event processing, retries, and lifecycle",
  },
];

export const Landingpage = () => {
  return (
    <section className="py-12 lg:py-20">
      <Head>
        <title>Home</title>
      </Head>

      {/* Hero — centered */}
      <div className="mx-auto max-w-3xl text-center">
        <h1 className="text-4xl font-extrabold tracking-[-0.02em] sm:text-5xl lg:text-6xl">
          One unified API for{" "}
          <span className="text-primary">global settlements</span>
        </h1>

        <Typography className="mx-auto mt-6 max-w-2xl">
          <p className="text-foreground/70 text-lg">
            Production-grade APIs for remittance, trading, checkout, wallet,
            and payout operations. Integrate once, authenticate every
            request, and move funds with full lifecycle visibility.
          </p>
        </Typography>

        <div className="mt-8 flex justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/getting-started">Get started</Link>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <Link to="/api-reference">API Reference</Link>
          </Button>
        </div>
      </div>

      {/* API products layout */}
      <div className="mx-auto mt-16 max-w-6xl space-y-4">
        {/* First Row: 3 items */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {firstRowProducts.map((product) => (
            <Link
              key={product.href}
              to={product.href}
              className="group border-border bg-card hover:border-primary/40 flex flex-col gap-3 rounded-2xl border p-5 transition hover:shadow-md"
            >
              <div className="bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground flex h-9 w-9 items-center justify-center rounded-lg transition">
                {product.icon}
              </div>
              <div>
                <p className="text-foreground font-semibold">{product.label}</p>
                <p className="text-foreground/60 mt-1 text-sm">
                  {product.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Second Row: 2 items centered */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          {secondRowProducts.map((product) => (
            <Link
              key={product.href}
              to={product.href}
              className="group border-border bg-card hover:border-primary/40 flex flex-col gap-3 rounded-2xl border p-5 transition hover:shadow-md sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
            >
              <div className="bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground flex h-9 w-9 items-center justify-center rounded-lg transition">
                {product.icon}
              </div>
              <div>
                <p className="text-foreground font-semibold">{product.label}</p>
                <p className="text-foreground/60 mt-1 text-sm">
                  {product.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="mx-auto mt-12 flex flex-wrap justify-center gap-3">
        {quickLinks.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="group border-border bg-card hover:border-primary/40 hover:bg-primary/5 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition"
          >
            <span className="text-foreground/80 group-hover:text-primary font-medium">
              {link.label}
            </span>
            <span className="text-foreground/40">—</span>
            <span className="text-foreground/50">{link.description}</span>
          </Link>
        ))}
      </div>

      {/* LLM-ready docs */}
      <div className="mx-auto mt-16 max-w-2xl rounded-2xl border border-border bg-card p-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-primary">
            <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4Z" />
            <path d="M18 14c2 1 3 3 3 5v2H3v-2c0-2 1-4 3-5" />
            <circle cx="12" cy="6" r="1" />
          </svg>
          <h3 className="text-foreground text-lg font-semibold">LLM-Ready Documentation</h3>
        </div>
        <p className="text-foreground/60 mt-2 text-sm">
          Feed our docs directly into your AI tools and coding assistants.
        </p>
        <div className="mt-4 flex justify-center gap-3">
          <a
            href="/llms.txt"
            target="_blank"
            rel="noopener noreferrer"
            className="border-border bg-card hover:border-primary/40 hover:bg-primary/5 inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium text-foreground/80 transition"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6" />
            </svg>
            llms.txt
          </a>
          <a
            href="/llms-full.txt"
            target="_blank"
            rel="noopener noreferrer"
            className="border-border bg-card hover:border-primary/40 hover:bg-primary/5 inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium text-foreground/80 transition"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6" />
              <path d="M16 13H8M16 17H8M10 9H8" />
            </svg>
            llms-full.txt
          </a>
        </div>
      </div>
    </section>
  );
};
